
// WHY: копируется VERBATIM в skin-editor скриптом scripts/sync-renderer.sh — править здесь, импортов из app не добавлять
export interface RSprite {
  canvas: CanvasImageSource;
  sx?: number;
  sy?: number;
  sw?: number;
  sh?: number;
  bx: number;
  by: number;
  bw: number;
  bh: number;
}

function blit(ctx: CanvasRenderingContext2D, s: RSprite, dx: number, dy: number): void {
  if (s.sx != null && s.sw != null && s.sh != null) {
    ctx.drawImage(s.canvas, s.sx, s.sy ?? 0, s.sw, s.sh, dx, dy, s.sw, s.sh);
  } else {
    ctx.drawImage(s.canvas, dx, dy);
  }
}
export type RStance = 'rifle' | 'awp';
export interface RXY {
  x: number;
  y: number;
}
export interface RUp {
  x: number;
  y: number;
  scale: number;
}
export interface RRun {
  up: RUp;
  scale: number;
}
export interface RAnchors {
  hand: Record<RStance, RXY>;
  up: Record<RStance, RUp>;
  pivot: Record<RStance, RXY>;
  scale: number;
  body: Record<RStance, number>;
  run: Record<RStance, RRun>;
}

export const WEAPON_REL_W = 0.744;
export const AWP_REL_W = 1.1115;
export const WEAPON_GRIP = { x: 0.34, y: 0.52 };
export const UPPER_AIM_LEAN = 0.75;
export const RUN_FPS = 4;

const relWOf = (st: RStance) => (st === 'awp' ? AWP_REL_W : WEAPON_REL_W);
const KNIFE_REL_W = 0.315;
const KNIFE_GRIP = { x: 0.66, y: 0.72 };

export interface FighterParts {
  legs: RSprite;
  upper: RSprite;
  runFrames?: RSprite[] | null;
  weapon: RSprite;
}

export interface FighterDraw {
  parts: FighterParts;
  A: RAnchors;
  stance: RStance;
  x: number;
  y: number;
  tokenH: number;
  dir?: 1 | -1;
  aim?: number;
  bob?: number;
  sway?: number;
  recoilX?: number;
  moving?: boolean;
  reverseRun?: boolean;
  time?: number;
  muzzle?: RXY;
  knife?: boolean;
  knifeSwing?: number;
}

function runFrameOf(d: FighterDraw): RSprite {
  const frames = d.parts.runFrames;
  if (!frames || frames.length === 0) return d.parts.legs;
  let idx = Math.floor((d.time ?? 0) * RUN_FPS) % frames.length;
  if (d.reverseRun) idx = frames.length - 1 - idx;
  return frames[idx] ?? frames[0];
}

function splitStandingPx(legs: RSprite, upper: RSprite, up: RUp, pivot: RXY): number {
  return up.y * legs.bh + pivot.y * upper.bh * up.scale;
}

const normAngle = (a: number) => {
  const t = ((a + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);
  return t - Math.PI;
};
const localAimOf = (aim: number, dir: 1 | -1) => normAngle(dir === 1 ? aim : Math.PI - aim);

export interface FighterGeom {
  scale: number;
  legs: RSprite;
  upper: RSprite;
  body: RSprite;
  us: number;
  join: RXY;
  handPt: RXY;
  weaponAngle: number;
  weaponSizeRef: number;
  upperTilt: number;
  pivot: RXY;
  muzzle: RXY;
}

export function fighterGeometry(d: FighterDraw): FighterGeom {
  const dir = d.dir ?? 1;
  const aim = d.aim ?? 0;
  const stance = d.stance;
  const hand = d.A.hand[stance];

  const runMode = !!d.moving;
  const legs = runMode ? runFrameOf(d) : d.parts.legs;
  const upper = d.parts.upper;
  const runRig = d.A.run[stance];
  const up = runMode ? runRig.up : d.A.up[stance];
  const pivot = d.A.pivot[stance];
  const standingPx = splitStandingPx(legs, upper, up, pivot);
  const bodyMul = runMode ? runRig.scale : d.A.body[stance];
  const scale = (d.tokenH / standingPx) * bodyMul;
  const us = scale * up.scale;
  const localAim = localAimOf(aim, dir);
  const upperTilt = UPPER_AIM_LEAN * localAim;

  const breath = runMode || d.time == null ? 0 : Math.sin(d.time * 1.6 + d.x * 0.03) * 0.02 * legs.bh * scale;
  const joinX = up.x * legs.bw * scale;
  const joinY = -up.y * legs.bh * scale - breath;
  const hlx = (hand.x - pivot.x) * upper.bw * us;
  const hly = (hand.y - pivot.y) * upper.bh * us;
  const cu = Math.cos(upperTilt);
  const su = Math.sin(upperTilt);
  const handPt = { x: joinX + (hlx * cu - hly * su), y: joinY + (hlx * su + hly * cu) };

  const weapon = d.parts.weapon;
  const rs = (standingPx * scale * relWOf(stance)) / weapon.bw;
  const mz = d.muzzle ?? { x: 1, y: WEAPON_GRIP.y };
  const mox = (mz.x - WEAPON_GRIP.x) * weapon.bw * rs;
  let moy = (mz.y - WEAPON_GRIP.y) * weapon.bh * rs;
  const mc = Math.cos(localAim);
  const ms = Math.sin(localAim);
  if (mc < 0) moy = -moy;
  const mlx = handPt.x + (mox * mc - moy * ms);
  const mly = handPt.y + (mox * ms + moy * mc);

  return {
    scale,
    legs,
    upper,
    body: legs,
    us,
    join: { x: joinX, y: joinY },
    handPt,
    weaponAngle: localAim,
    weaponSizeRef: standingPx * scale,
    upperTilt,
    pivot,
    muzzle: { x: d.x + dir * mlx, y: d.y + mly },
  };
}

export function drawFighterBody(ctx: CanvasRenderingContext2D, d: FighterDraw, g = fighterGeometry(d)): void {
  const dir = d.dir ?? 1;
  const { legs, upper } = g;
  ctx.save();
  ctx.translate(d.x, d.y);
  ctx.scale(dir, 1);
  ctx.save();
  ctx.scale(g.scale, g.scale);
  blit(ctx, legs, -legs.bw / 2, -legs.bh);
  ctx.restore();
  ctx.translate(g.join.x, g.join.y);
  ctx.rotate(g.upperTilt);
  ctx.scale(g.us, g.us);
  blit(ctx, upper, -g.pivot.x * upper.bw, -g.pivot.y * upper.bh);
  ctx.restore();
}

export function drawFighterWeapon(ctx: CanvasRenderingContext2D, d: FighterDraw, g = fighterGeometry(d)): void {
  const dir = d.dir ?? 1;
  const weapon = d.parts.weapon;
  const relW = d.knife ? KNIFE_REL_W : relWOf(d.stance);
  const grip = d.knife ? KNIFE_GRIP : WEAPON_GRIP;
  const rs = (g.weaponSizeRef * relW) / weapon.bw;
  ctx.save();
  ctx.translate(d.x, d.y);
  ctx.scale(dir, 1);
  ctx.translate(g.handPt.x, g.handPt.y);
  ctx.rotate(g.weaponAngle);
  if (d.knife && d.knifeSwing) ctx.rotate(d.knifeSwing);
  if (Math.cos(g.weaponAngle) < 0) ctx.scale(1, -1);
  ctx.scale(rs, rs);
  blit(ctx, weapon, -(weapon.bx + weapon.bw * grip.x), -(weapon.by + weapon.bh * grip.y));
  ctx.restore();
}

export function drawFighter(ctx: CanvasRenderingContext2D, d: FighterDraw): FighterGeom {
  const g = fighterGeometry(d);
  drawFighterBody(ctx, d, g);
  drawFighterWeapon(ctx, d, g);
  return g;
}
